import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import SheetSubjectListPage from "../components/pages/SheetSubjectListPage";
import { RootTabScreenProps } from "../types";
import { ProjectContext } from "../data/projectContext";
import {
  isCharacterCategory,
  SheetCharacterCategory,
} from "@atgof-common/sheet";
import { ExportButton, pdfExportPhrases } from "../components/ExportButton";
import { useSeasons } from "../data/useSeasons";
import {
  CollectionListGroups,
  renderListFilterComponent,
  useFilterListHeader,
} from "../components/CollectionList";
import {
  compareSheetSubject,
  labelForSheetSubject,
  searchTokensForSheetSubject,
  SheetSubject,
  toSheetSubject,
} from "@atgof-common/sheet-subject";
import { actualPerformerKind, PerformerKind } from "@atgof-common/subject";
import { PhraseKey } from "@atgof-common/phrases";
import { LayoutChangeEvent, View } from "react-native";
import { IconButton, ProgressBar, Text, useTheme } from "react-native-paper";
import NavPage from "../components/pages/NavPage";
import { FlashList, RenderTarget } from "@shopify/flash-list";
import { useDocuments } from "../data/useDocuments";
import { useProject } from "../data/useProject";
import { sheetSubjectCollection } from "../data/sheet";
import { DocumentSnapshot } from "@atgof-firebase/types";
import dayjs from "dayjs";
import { filmingDate } from "@atgof-common/day";
import { LanguageContext } from "@atgof-common/language";
import { MD3Colors } from "react-native-paper/lib/typescript/types";
import { useNavigation } from "@react-navigation/native";

const kSelectedCharacterGroup = "selectedCharacterGroup";

function groupFor(
  performerKind: PerformerKind,
  label: PhraseKey,
): CollectionListGroups<SheetSubject>["items"][number] {
  return {
    key: performerKind,
    predicate: (item) =>
      actualPerformerKind(item.displayId, item.performerKind) === performerKind,
    label,
  };
}

type DOODsRow = {
  firstColumn: SheetSubject | string[];
  dayEntries: (string | string[] | undefined)[];
  rowIndex: number;
};

type ExtraData = {
  colors: MD3Colors;
  moveBackward: (() => void) | undefined;
  moveForward: (() => void) | undefined;
};

const paddingVertical = 10;
const marginHorizontal = 10;

const firstColumnWidth = 260;
const dayEntryWidth = 40;

function renderDOODsRow({
  item,
  extraData,
}: {
  item: DOODsRow;
  target: RenderTarget;
  extraData?: ExtraData;
}) {
  const { firstColumn, dayEntries, rowIndex } = item;
  return (
    <View
      style={{
        flexDirection: "row",
        backgroundColor:
          rowIndex % 2 == 0
            ? extraData?.colors.background
            : extraData?.colors.elevation.level1,
        alignItems: "stretch",
      }}
    >
      <View
        style={{
          width: firstColumnWidth,
          borderRightWidth: 1,
          borderRightColor: extraData?.colors.outline,
          paddingHorizontal: 8,
          marginHorizontal,
          paddingVertical,
        }}
      >
        {("displayName" in firstColumn
          ? [labelForSheetSubject(firstColumn)]
          : firstColumn
        ).map((text, i) => (
          <Text
            key={i}
            style={{
              textAlign: "displayName" in firstColumn ? undefined : "right",
            }}
          >
            {text}
          </Text>
        ))}
      </View>
      {extraData?.moveBackward ? (
        <View style={{ width: dayEntryWidth + marginHorizontal * 2 }}>
          {rowIndex == 0 ? (
            <IconButton icon="chevron-left" onPress={extraData.moveBackward} />
          ) : null}
        </View>
      ) : null}
      {dayEntries.map((s, i) => (
        <View
          key={i}
          style={{
            width: dayEntryWidth + (s === undefined ? marginHorizontal * 2 : 0),
            marginHorizontal: s === undefined ? undefined : marginHorizontal,
            paddingVertical: s === undefined ? undefined : paddingVertical,
            backgroundColor:
              s === undefined ? extraData?.colors.elevation.level2 : undefined,
          }}
        >
          {(typeof s === "string" ? [s] : (s ?? [])).map((text, j) => (
            <Text key={j} style={{ textAlign: "center" }}>
              {text}
            </Text>
          ))}
        </View>
      ))}
      {extraData?.moveForward ? (
        <View style={{ width: dayEntryWidth + marginHorizontal * 2 }}>
          {rowIndex == 0 ? (
            <IconButton icon="chevron-right" onPress={extraData.moveForward} />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function dayEntriesForPerformer(
  { displayName, performerKind }: SheetSubject,
  days: (DocumentSnapshot | undefined)[],
) {
  let entries: (string | undefined)[] = days.map((doc) =>
    doc
      ? doc
          .get(
            performerKind === "cast"
              ? "cast"
              : "nonCast.sa" /* TODO Support nonCast.stunt as needed */,
          )
          ?.includes(displayName)
        ? "W"
        : ""
      : undefined,
  );
  const firstI = entries.findIndex((x) => !!x?.length);
  if (firstI != -1) entries[firstI] = "S" + entries[firstI];
  const lastI = entries.findLastIndex((x) => !!x?.length);
  if (lastI != -1) entries[lastI] = entries[lastI] + "F";
  return entries;
}

type DayEntry = {
  date: dayjs.Dayjs;
  filmingDay?: number;
  doc?: DocumentSnapshot;
};

function DOODsTable({
  groups,
}: {
  groups: CollectionListGroups<SheetSubject>;
}) {
  const { ph } = useContext(LanguageContext);
  const { colors } = useTheme();
  const project = useProject();
  const flashList = useRef<FlashList<DOODsRow>>(null);
  const dayDocs = useDocuments(project?.collection("days"));
  const days = useMemo<DayEntry[] | undefined>(() => {
    if (!dayDocs) return;
    let days: DayEntry[] = [];
    let prevDate: dayjs.Dayjs | undefined;
    let filmingDay = 1;
    for (const doc of dayDocs) {
      const d = filmingDate(doc);
      if (!d) continue;
      const date = dayjs(d);
      while (prevDate && prevDate.add(1, "day").isBefore(date)) {
        prevDate = prevDate.add(1, "day");
        days.push({ date: prevDate });
      }
      days.push({ date, doc, filmingDay });
      prevDate = date;
      filmingDay++;
    }
    return days;
  }, [dayDocs]);
  const [numberOfDaysToShow, setNumberOfDaysToShow] = useState(0);
  const onLayout = (evt: LayoutChangeEvent) => {
    setNumberOfDaysToShow(
      Math.floor(
        (evt.nativeEvent.layout.width -
          (2 * marginHorizontal + firstColumnWidth)) /
          (2 * marginHorizontal + dayEntryWidth),
      ),
    );
  };
  const [firstDayIndexToShow, setFirstDayIndexToShow] = useState(0);
  const widthInDays =
    numberOfDaysToShow -
    ((firstDayIndexToShow ? 1 : 0) +
      (firstDayIndexToShow + numberOfDaysToShow < (days?.length ?? 0) ? 1 : 0));
  const extraData = useMemo(
    () => ({
      colors,
      moveBackward: firstDayIndexToShow
        ? () => {
            setFirstDayIndexToShow((i) => Math.max(0, i - widthInDays));
            flashList.current?.scrollToOffset({ offset: 0 }); // Because FlashList doesn't seem to refresh sticky rows properly
          }
        : undefined,
      moveForward:
        days?.length && firstDayIndexToShow + widthInDays < days.length
          ? () => {
              setFirstDayIndexToShow((i) => i + widthInDays);
              flashList.current?.scrollToOffset({ offset: 0 }); // Because FlashList doesn't seem to refresh sticky rows properly
            }
          : undefined,
    }),
    [colors, firstDayIndexToShow, widthInDays, days?.length],
  );
  const performers = useDocuments(
    project ? sheetSubjectCollection(project, "character") : undefined,
  );
  const { filter, filterUIData } = useFilterListHeader(groups);
  const ListFilterComponent = useMemo(
    () => renderListFilterComponent(filterUIData),
    [filterUIData],
  );
  const data = useMemo<DOODsRow[]>(
    () => [
      {
        rowIndex: 0,
        firstColumn: ["day-month", "day-of-week", "filming-day"].map(
          (k) => ph(k) as string,
        ),
        dayEntries: (days ?? [])
          .slice(firstDayIndexToShow, firstDayIndexToShow + widthInDays)
          .map(({ date, filmingDay }) => [
            date.format("DD/MM"),
            date.format("ddd"),
            filmingDay?.toString() ?? "",
          ]),
      },
      ...(performers ?? [])
        .map((doc) => toSheetSubject(doc))
        .filter((sheetSubject) =>
          filter(sheetSubject, searchTokensForSheetSubject),
        )
        .sort(compareSheetSubject)
        .map((sheetSubject, i) => ({
          rowIndex: 1 + i,
          firstColumn: sheetSubject,
          dayEntries: dayEntriesForPerformer(
            sheetSubject,
            (days ?? []).map(({ doc }) => doc),
          ).slice(firstDayIndexToShow, firstDayIndexToShow + widthInDays),
        })),
    ],
    [days, firstDayIndexToShow, widthInDays, performers, filter, ph],
  );
  const ListEmptyComponent = useMemo(
    () => (days && performers ? undefined : <ProgressBar indeterminate />),
    [days && performers],
  );
  const ListFooterComponent = useMemo(
    () =>
      data.length
        ? renderDOODsRow({ item: data[0], target: "StickyHeader", extraData })
        : undefined,
    [data, extraData],
  );
  return (
    <View style={{ flex: 1 }} onLayout={onLayout}>
      <FlashList
        ref={flashList}
        ListHeaderComponent={ListFilterComponent}
        ListFooterComponent={ListFooterComponent}
        ListEmptyComponent={ListEmptyComponent}
        renderItem={renderDOODsRow}
        data={data}
        extraData={extraData}
        stickyHeaderIndices={[0]}
      />
    </View>
  );
}

export default function CharactersScreen({}: RootTabScreenProps<"characters">) {
  const { characters, adminCategories, categories } =
    React.useContext(ProjectContext);
  const adminCharacterCategories = adminCategories.filter(
    isCharacterCategory,
  ) as SheetCharacterCategory[];
  const seasons = useSeasons(false);
  const [showDoods, setShowDoods] = useState(false);
  const navigation = useNavigation();
  useEffect(() => {
    const onBlur = () => {
      setShowDoods(false);
    };
    navigation.addListener("blur", onBlur);
    return () => {
      navigation.removeListener("blur", onBlur);
    };
  }, [navigation]);
  const headerRight = React.useMemo(
    () => (
      <View style={{ flexDirection: "row", gap: 4 }}>
        <IconButton
          icon="clock-end"
          onPress={() => setShowDoods((show) => !show)}
          mode={showDoods ? "contained" : undefined}
        />
        {adminCharacterCategories.length && seasons?.length ? (
          <ExportButton
            actions={[
              {
                kind: "sheetSubject",
                exportData: {
                  season: seasons[0].ref,
                  categories: adminCharacterCategories,
                  subjectKind: "character",
                },
                phrases: pdfExportPhrases,
              },
            ]}
          />
        ) : null}
      </View>
    ),
    [
      adminCharacterCategories.join("\n"),
      seasons?.length ? seasons[0].ref.path : undefined,
      showDoods,
    ],
  );
  const groups = React.useMemo<CollectionListGroups<SheetSubject>>(
    () => ({
      selection: {
        default: categories.every(isCharacterCategory) ? "cast" : "all",
        persistWithKey: kSelectedCharacterGroup,
      },
      items: [
        { key: "all", label: "all", predicate: (x) => !!x },
        groupFor("cast", "cast"),
        groupFor("sa", "supporting-artists-short"),
        groupFor("stunt", "stunt-performers"),
      ],
    }),
    [categories],
  );
  return showDoods ? (
    <NavPage fullScreen headerRight={headerRight}>
      <DOODsTable groups={groups} />
    </NavPage>
  ) : (
    <SheetSubjectListPage
      kind="character"
      pinnedSubjects={characters}
      headerRight={headerRight}
      groups={groups}
    />
  );
}
